import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { sendRequest } from "../services/api";
// reCAPTCHA removed
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../analytics/analytics';
import { sanitizePlainText, isValidDutchPhone, isValidName, clamp } from '../utils/validation';
import CheckIcon from './icons/CheckIcon';
import logoImage from '../assets/logo.png';

// Types
interface PackageInfo {
  price: string;
  features: React.ReactNode[];
  sectionTitle: React.ReactNode;
}

interface TariffModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageInfo: PackageInfo | null;
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(5px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-50px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 2px solid #f0f0f0;
  position: relative;
  
  h5 {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--primary-blue);
    margin: 1.5rem 0 0 0;
    text-align: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #f0f0f0;
    color: var(--primary-orange);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const PackageInfoSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid #dee2e6;
  
  h6 {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--primary-blue);
    margin-bottom: 1rem;
  }
  
  .price {
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary-orange);
    margin-bottom: 1rem;
  }
  
  .features {
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
        
        i {
          color: #28a745;
          font-size: 1.1rem;
        }
      }
    }
  }
`;

const FormSection = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
  
  label {
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    
    .required {
      color: #dc3545;
    }
  }
`;

const FormInput = styled.input<{ hasError?: boolean; isValid?: boolean }>`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid ${props => props.hasError ? '#dc3545' : props.isValid ? '#28a745' : '#e1e5e9'};
  border-radius: 12px;
  font-size: 1rem;
  background: ${props => props.hasError ? '#fff5f5' : props.isValid ? '#f0fff4' : '#f8f9fa'};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc3545' : '#ec5c2c'};
    background: white;
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(236, 92, 44, 0.1)'};
  }
  
  &::placeholder {
    color: #6c757d;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  font-weight: 500;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--primary-orange);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  p {
    color: var(--primary-blue);
    font-weight: 600;
    font-size: 1.1rem;
    margin: 0;
  }
`;

const ResponseMessage = styled.div`
  text-align: center;
  padding: 2rem;
  
  .logo {
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    img {
      height: 48px;
      width: auto;
      display: block; /* avoid baseline gap */
    }
  }
  
  .message {
    margin-bottom: 1.5rem;
    
    &.success {
      color: #28a745;
      background: #d4edda;
      border: 1px solid #c3e6cb;
      padding: 1rem;
      border-radius: 10px;
      
      i {
        font-size: 1.2rem;
        margin-right: 0.5rem;
      }
    }
    
    &.error {
      color: #dc3545;
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      padding: 1rem;
      border-radius: 10px;
      
      i {
        font-size: 1.2rem;
        margin-right: 0.5rem;
      }
    }
  }
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-orange);
    color: white;
    
    &:hover:not(:disabled) {
      background: #d44d26;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(236, 92, 44, 0.3);
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }
  `}
`;

const OkButton = styled(Button)`
  background: var(--primary-orange);
  color: white;
  
  &:hover {
    background: #d44d26;
    transform: translateY(-2px);
  }
`;

// Use centralized validators

const TariffModal: React.FC<TariffModalProps> = ({ isOpen, onClose, packageInfo }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [validation, setValidation] = useState({
    name: { isValid: false, hasError: false, message: '' },
    phone: { isValid: false, hasError: false, message: '' }
  });
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitLocked, setSubmitLocked] = useState(false);
  const [hasSanitizeMismatch, setHasSanitizeMismatch] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', phone: '' });
      setValidation({
        name: { isValid: false, hasError: false, message: '' },
        phone: { isValid: false, hasError: false, message: '' }
      });
      setIsSubmitEnabled(false);
      setLoading(false);
      setResponse(null);
      
      return () => {};
    }
  }, [isOpen]);

  // Validate form and update submit button state
  useEffect(() => {
    const nameSan = sanitizePlainText(formData.name, 50);
    const phoneSan = sanitizePlainText(formData.phone, 20);
    const mismatch = (nameSan !== formData.name) || (phoneSan !== formData.phone);
    setHasSanitizeMismatch(mismatch);
    const nameValid = isValidName(nameSan, 2, 25);
    const phoneValid = isValidDutchPhone(phoneSan);
    setIsSubmitEnabled(!mismatch && nameValid && phoneValid);
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Handle phone number formatting
    if (name === 'phone') {
      // Do not auto-sanitize or reformat; keep raw input per policy
      processedValue = value;
    }
    
    // Keep raw value; validation will block if sanitization changes it
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Real-time validation
    setTimeout(() => validateField(name, processedValue), 0);
  };

  const validateField = (fieldName: string, value: string) => {
    const trimmedValue = sanitizePlainText(value);
    
    if (fieldName === 'name') {
      if (trimmedValue.length === 0) {
        setValidation(prev => ({
          ...prev,
          name: { isValid: false, hasError: false, message: '' }
        }));
      } else if (!isValidName(trimmedValue, 2, 25) || sanitizePlainText(formData.name, 50) !== formData.name) {
        setValidation(prev => ({
          ...prev,
          name: { isValid: false, hasError: true, message: t('tariffs.modal.validation.name_min', { defaultValue: 'Name must be at least 2 characters' }) }
        }));
      } else {
        setValidation(prev => ({
          ...prev,
          name: { isValid: true, hasError: false, message: '' }
        }));
      }
    }
    
    if (fieldName === 'phone') {
      if (trimmedValue.length === 0) {
        setValidation(prev => ({
          ...prev,
          phone: { isValid: false, hasError: false, message: '' }
        }));
      } else if (!isValidDutchPhone(trimmedValue) || sanitizePlainText(formData.phone, 20) !== formData.phone) {
        setValidation(prev => ({
          ...prev,
          phone: { isValid: false, hasError: true, message: t('tariffs.modal.validation.phone_invalid', { defaultValue: 'Please enter a valid Dutch phone number' }) }
        }));
      } else {
        setValidation(prev => ({
          ...prev,
          phone: { isValid: true, hasError: false, message: '' }
        }));
      }
    }
  };

  const buildMessage = (): string => {
    if (!packageInfo) return '';
    const sectionTitle = typeof packageInfo.sectionTitle === 'string' ? packageInfo.sectionTitle : '';
    const featuresText = packageInfo.features.map((f) => (typeof f === 'string' ? f : '')).join(', ');
    
    return `${t('tariffs.modal.email_intro', { defaultValue: 'Tariff selection from the tariffs page' })}

${t('tariffs.modal.customer_details', { defaultValue: 'Customer details:' })}
${t('tariffs.modal.name', { defaultValue: 'Name' })}: ${formData.name}
${t('tariffs.modal.phone', { defaultValue: 'Phone' })}: ${formData.phone}

${t('tariffs.modal.selected_package', { defaultValue: 'Selected package:' })}
${t('tariffs.modal.package', { defaultValue: 'Package' })}: ${sectionTitle}
${t('tariffs.modal.price', { defaultValue: 'Price' })}: ${packageInfo.price}
${t('tariffs.modal.details', { defaultValue: 'Details' })}: ${featuresText}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    trackEvent('tariff_modal_submit_attempt', { enabled: isSubmitEnabled, hasPackage: !!packageInfo });
    if (!isSubmitEnabled || !packageInfo || submitLocked) {
      if (hasSanitizeMismatch) {
        setResponse({ type: 'error', message: t('validation.invalid_input_generic', { defaultValue: 'Invalid input detected. Please remove special characters and try again.' }) });
      }
      return;
    }

    setLoading(true);
    setSubmitLocked(true);
    
    try {
      const cleanName = clamp(sanitizePlainText(formData.name), 50);
      const cleanPhone = clamp(sanitizePlainText(formData.phone), 20);
      const message = buildMessage();
      const subject = t('tariffs.modal.email_subject', { price: packageInfo.price, name: cleanName, defaultValue: `A package was selected (${packageInfo.price}) - ${cleanName}` });
      const resp = await sendRequest('sendEmail', {
        name: cleanName,
        email: 'noreply@universaldrive.nl',
        phone: cleanPhone,
        subject,
        message,
        language: i18n.language || 'nl',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      }, 'POST');
      
      const okMessage = (typeof resp === 'string' ? resp : (resp && (resp.message || resp.msg))) || t('tariffs.modal.success_message', { name: formData.name, defaultValue: `Thank you for your request ${formData.name}! Your request was sent successfully! Our team will contact you soon!` }) as string;
      setResponse({
        type: 'success',
        message: okMessage
      });
      trackEvent('tariff_modal_submit_success', { price: packageInfo.price });
      
    } catch (error: any) {
      // Intentionally avoid console.error to keep browser console clean
      const status = error?.status;
      if (status === 429) {
        setResponse({ type: 'error', message: t('tariffs.modal.errors.rate_limit', { defaultValue: 'You have reached the submission limit. Please contact us by phone or WhatsApp.' }) });
        trackEvent('tariff_modal_submit_error', { status: 429 });
      } else {
        setResponse({
          type: 'error',
          message: t('tariffs.modal.errors.generic', { defaultValue: 'An error occurred. Please try again later.' })
        });
        trackEvent('tariff_modal_submit_error', { status: status || 0 });
      }
    } finally {
      setLoading(false);
      setSubmitLocked(false);
    }
  };

  const handleClose = () => {
    setResponse(null);
    setLoading(false);
    onClose();
  };

  const handleOkClick = () => {
    handleClose();
    // Reload page as in original implementation
    window.location.reload();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent('tariff_modal_open', {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <ModalContainer>
        <ModalHeader>
          <h5>{t('tariffs.modal.title', { name: (packageInfo?.sectionTitle as any) || t('tariffs.modal.default_title_name', { defaultValue: 'Driving lessons' }), defaultValue: `Request Package - ${(packageInfo?.sectionTitle as any) || 'Driving lessons'}` })}</h5>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {loading ? (
            <LoadingIndicator>
              <div className="spinner"></div>
              <p>{t('tariffs.modal.loading', { defaultValue: 'Please wait...' })}</p>
            </LoadingIndicator>
          ) : response ? (
            <ResponseMessage>
              <div className="logo">
                <img src={logoImage} alt="UniversalDrive Logo" />
              </div>
              <div className={`message ${response.type}`}>
                <i className={`fa ${response.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                {response.message}
              </div>
              <OkButton onClick={handleOkClick}>{t('tariffs.modal.buttons.ok', { defaultValue: 'OK' })}</OkButton>
            </ResponseMessage>
          ) : (
            <>
              {packageInfo && (
                <PackageInfoSection>
                  <h6>{t('tariffs.modal.selected_package', { defaultValue: 'Selected package:' })}</h6>
                  <div className="price">{packageInfo.price}</div>
                  <div className="features">
                    <strong>{t('tariffs.modal.included', { defaultValue: 'Included:' })}</strong>
                    <ul>
                      {packageInfo.features.map((feature, index) => (
                        <li key={index} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <CheckIcon size={18} color="#28a745" aria-hidden="true" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </PackageInfoSection>
              )}
              
              <FormSection onSubmit={handleSubmit}>
                <FormGroup>
                  <label htmlFor="customerName">
                    {t('tariffs.modal.name', { defaultValue: 'Name' })} <span className="required">*</span>
                  </label>
                  <FormInput
                    id="customerName"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={String(t('tariffs.modal.placeholders.name', { defaultValue: 'Your name' }))}
                    hasError={validation.name.hasError}
                    isValid={validation.name.isValid}
                    maxLength={25}
                    required
                  />
                  {validation.name.hasError && (
                    <ErrorMessage>{validation.name.message}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <label htmlFor="customerPhone">
                    {t('tariffs.modal.phone', { defaultValue: 'Phone' })} <span className="required">*</span>
                  </label>
                  <FormInput
                    id="customerPhone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={String(t('tariffs.modal.placeholders.phone', { defaultValue: 'Your phone number' }))}
                    hasError={validation.phone.hasError}
                    isValid={validation.phone.isValid}
                    required
                  />
                  {validation.phone.hasError && (
                    <ErrorMessage>{validation.phone.message}</ErrorMessage>
                  )}
                </FormGroup>
                {/* Anti-bot field removed */}
              </FormSection>
            </>
          )}
        </ModalBody>
        
        {!loading && !response && (
          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              {t('tariffs.modal.buttons.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
            >
              {t('tariffs.modal.buttons.send', { defaultValue: 'Send' })}
            </Button>
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TariffModal;

